import { useState, type ReactNode } from "react";

import { MONOLITH_BOARD_STYLE } from "@/components/dashboard/monolithBoardStyle";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/classNames";

/* ---------------------------------------------------------------------------
 * Shared helpers
 * -------------------------------------------------------------------------*/

function SectionTitle({ children, id }: { children: ReactNode; id: string }) {
  return (
    <h2
      id={id}
      className="scroll-mt-6 text-xl font-semibold uppercase tracking-[0.2em] [font-family:var(--color-lab-display),system-ui,sans-serif]"
    >
      {children}
    </h2>
  );
}

const SAMPLE_FIELDS = [
  { label: "Name", value: "The Hollow Spire" },
  { label: "Hook", value: "Strange lights flicker from the upper windows each full moon, and shepherds report missing livestock within a league of the ruin." },
  { label: "Visibility", value: "Visible to players" },
  { label: "Coordinates", value: "(4, −2)" },
] as const;

/* ---------------------------------------------------------------------------
 * Variation selector
 * -------------------------------------------------------------------------*/

const VARIATION_KEYS = ["stacked", "inset-label", "ruled", "plaque", "field-card"] as const;
type VariationKey = (typeof VARIATION_KEYS)[number];

const VARIATION_META: Record<VariationKey, { title: string; description: string }> = {
  stacked: {
    title: "Stacked",
    description: "Horizontal rules separating sections inside a single card. Uppercase micro-label over content, optional inline action. This is the current POI detail pattern.",
  },
  "inset-label": {
    title: "Inset label",
    description: "The section label sits inside a tinted sidebar gutter on the left, with the content flowing to the right. Creates a ledger-book feel.",
  },
  ruled: {
    title: "Ruled",
    description: "No outer card — each section has a heavy top rule with the label inlined. Content sits directly on the page surface. Minimal and document-like.",
  },
  plaque: {
    title: "Plaque",
    description: "Each section gets its own dark header bar with the label reversed out in light text, content below in a light body. Evokes brass-plate labeling on a cabinet.",
  },
  "field-card": {
    title: "Field card",
    description: "Standalone bordered cards for each field, stacked with a gap. The label sits inside a notched tab that breaks the top border. Modular and rearrangeable.",
  },
};

/* ---------------------------------------------------------------------------
 * Variation A — Stacked (current POI pattern)
 * -------------------------------------------------------------------------*/

function StackedVariation() {
  return (
    <div className="border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[12px_12px_0_rgba(19,19,19,0.14)]">
      {SAMPLE_FIELDS.map((field, i) => (
        <div
          key={field.label}
          className={cn(
            "p-5 sm:p-8",
            i < SAMPLE_FIELDS.length - 1 && "border-b-2 border-neutral-950/15",
          )}
        >
          <div className="flex items-center justify-between">
            <label className="block text-[10px] uppercase tracking-[0.34em] text-neutral-600">
              {field.label}
            </label>
            {i < 2 && (
              <button
                type="button"
                className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 hover:text-neutral-800"
              >
                Edit
              </button>
            )}
          </div>
          {i === 0 ? (
            <h3 className="mt-2 text-2xl font-black uppercase tracking-[-0.03em] text-neutral-950">
              {field.value}
            </h3>
          ) : (
            <p className="mt-2 text-sm leading-relaxed text-neutral-700">{field.value}</p>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Variation B — Inset label
 * -------------------------------------------------------------------------*/

function InsetLabelVariation() {
  return (
    <div className="border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[12px_12px_0_rgba(19,19,19,0.14)]">
      {SAMPLE_FIELDS.map((field, i) => (
        <div
          key={field.label}
          className={cn(
            "flex",
            i < SAMPLE_FIELDS.length - 1 && "border-b-2 border-neutral-950/15",
          )}
        >
          <div className="flex w-28 shrink-0 items-start border-r-2 border-neutral-950/15 bg-neutral-950/[0.035] p-4 sm:w-36 sm:p-6">
            <label className="block text-[10px] uppercase tracking-[0.34em] text-neutral-600">
              {field.label}
            </label>
          </div>
          <div className="min-w-0 flex-1 p-4 sm:p-6">
            {i === 0 ? (
              <h3 className="text-xl font-black uppercase tracking-[-0.03em] text-neutral-950">
                {field.value}
              </h3>
            ) : (
              <p className="text-sm leading-relaxed text-neutral-700">{field.value}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Variation C — Ruled
 * -------------------------------------------------------------------------*/

function RuledVariation() {
  return (
    <div className="space-y-8">
      {SAMPLE_FIELDS.map((field, i) => (
        <div key={field.label}>
          <div className="flex items-baseline gap-4 border-t-[3px] border-neutral-950 pt-4">
            <label className="shrink-0 text-[10px] uppercase tracking-[0.34em] text-neutral-600">
              {field.label}
            </label>
            <span className="h-px flex-1 bg-neutral-950/10" />
            {i < 2 && (
              <button
                type="button"
                className="shrink-0 text-[10px] uppercase tracking-[0.14em] text-neutral-500 hover:text-neutral-800"
              >
                Edit
              </button>
            )}
          </div>
          {i === 0 ? (
            <h3 className="mt-3 text-2xl font-black uppercase tracking-[-0.03em] text-neutral-950">
              {field.value}
            </h3>
          ) : (
            <p className="mt-3 text-sm leading-relaxed text-neutral-700">{field.value}</p>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Variation D — Plaque
 * -------------------------------------------------------------------------*/

function PlaqueVariation() {
  return (
    <div className="space-y-6">
      {SAMPLE_FIELDS.map((field, i) => (
        <div
          key={field.label}
          className="overflow-hidden border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[8px_8px_0_rgba(19,19,19,0.10)]"
        >
          <div className="flex items-center justify-between border-b-[3px] border-neutral-950 bg-neutral-950 px-5 py-2.5 sm:px-6">
            <label className="text-[10px] uppercase tracking-[0.34em] text-[#fff7ec]/60">
              {field.label}
            </label>
            {i < 2 && (
              <button
                type="button"
                className="text-[10px] uppercase tracking-[0.14em] text-[#fff7ec]/40 hover:text-[#fff7ec]/80"
              >
                Edit
              </button>
            )}
          </div>
          <div className="p-5 sm:p-6">
            {i === 0 ? (
              <h3 className="text-xl font-black uppercase tracking-[-0.03em] text-neutral-950">
                {field.value}
              </h3>
            ) : (
              <p className="text-sm leading-relaxed text-neutral-700">{field.value}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Variation E — Field card
 * -------------------------------------------------------------------------*/

function FieldCardVariation() {
  return (
    <div className="space-y-6">
      {SAMPLE_FIELDS.map((field, i) => (
        <div key={field.label} className="relative">
          {/* Notched label tab */}
          <div className="absolute -top-px left-5 z-10 -translate-y-1/2 border-2 border-neutral-950 bg-[#fff2d9] px-3 py-1 sm:left-7">
            <label className="block text-[10px] uppercase tracking-[0.34em] text-neutral-700">
              {field.label}
            </label>
          </div>
          <div className="border-[3px] border-neutral-950 bg-[#fffaf1] px-5 pb-5 pt-7 shadow-[6px_6px_0_rgba(19,19,19,0.10)] sm:px-7 sm:pb-6 sm:pt-8">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                {i === 0 ? (
                  <h3 className="text-xl font-black uppercase tracking-[-0.03em] text-neutral-950">
                    {field.value}
                  </h3>
                ) : (
                  <p className="text-sm leading-relaxed text-neutral-700">{field.value}</p>
                )}
              </div>
              {i < 2 && (
                <button
                  type="button"
                  className="shrink-0 text-[10px] uppercase tracking-[0.14em] text-neutral-500 hover:text-neutral-800"
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Variation map
 * -------------------------------------------------------------------------*/

const VARIATION_COMPONENTS: Record<VariationKey, () => ReactNode> = {
  stacked: StackedVariation,
  "inset-label": InsetLabelVariation,
  ruled: RuledVariation,
  plaque: PlaqueVariation,
  "field-card": FieldCardVariation,
};

/* ---------------------------------------------------------------------------
 * Page export
 * -------------------------------------------------------------------------*/

export function SectionsShowcaseClient() {
  const [active, setActive] = useState<VariationKey>("stacked");
  const ActiveComponent = VARIATION_COMPONENTS[active];

  return (
    <div className="mx-auto min-h-full max-w-5xl px-5 py-12 pb-24 text-neutral-950 sm:px-8">
      <header className="border-b-[3px] border-neutral-950 pb-10">
        <p className="text-[11px] uppercase tracking-[0.28em] text-neutral-600">
          Design system &middot; Sections
        </p>
        <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif] sm:text-5xl">
          Section patterns
        </h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-neutral-800">
          Repeating section blocks with a title label, content, and optional actions.
          Five treatments for the same data — choose the one that fits the context.
        </p>
      </header>

      {/* Variation switcher */}
      <nav className="mt-10 flex flex-wrap gap-2" aria-label="Section variations">
        {VARIATION_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setActive(key)}
            className={cn(
              "border-2 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors",
              active === key
                ? "border-neutral-950 bg-neutral-950 text-[#fff7ec] shadow-[4px_4px_0_rgba(19,19,19,0.2)]"
                : "border-neutral-950/30 bg-transparent text-neutral-600 hover:border-neutral-950 hover:text-neutral-950",
            )}
          >
            {VARIATION_META[key].title}
          </button>
        ))}
      </nav>

      {/* Active variation info */}
      <div className="mt-8 space-y-2">
        <h2 className="text-lg font-bold uppercase tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif]">
          {VARIATION_META[active].title}
        </h2>
        <p className="max-w-2xl text-sm leading-relaxed text-neutral-700">
          {VARIATION_META[active].description}
        </p>
      </div>

      {/* Preview */}
      <div className="mt-10 rounded-sm bg-[#f7f3eb]/60 p-6 sm:p-10">
        <ActiveComponent />
      </div>

      {/* All variations gallery */}
      <div className="mt-20 space-y-16">
        <SectionTitle id="all-variations">All variations</SectionTitle>
        <p className="-mt-12 max-w-2xl text-neutral-800">
          Side-by-side overview of every treatment using the same sample data.
        </p>
        {VARIATION_KEYS.map((key) => {
          const Comp = VARIATION_COMPONENTS[key];
          return (
            <section key={key} className="space-y-4">
              <div className="flex items-baseline gap-4 border-t-2 border-neutral-950/10 pt-4">
                <h3 className="text-[10px] font-semibold uppercase tracking-[0.28em] text-neutral-600">
                  {VARIATION_META[key].title}
                </h3>
                <span className="h-px flex-1 bg-neutral-950/8" />
              </div>
              <p className="max-w-xl text-sm leading-relaxed text-neutral-700">
                {VARIATION_META[key].description}
              </p>
              <div className="rounded-sm bg-[#f7f3eb]/60 p-6 sm:p-10">
                <Comp />
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
